import type { BankTransactionSuggestionJournalEntry } from "./types";

export type SmartLinkSelection = {
  ids: number[];
  dateProfile: string[];
};

export type SmartLinkRequest = {
  suggestions: BankTransactionSuggestionJournalEntry[];
  transactionAmount: number;
  transactionDate: string | null;
  transactionCreatedAt: string;
  channelBalance: number;
  cutoffDays?: number;
};

export type SmartLinkResult = {
  selection: SmartLinkSelection | null;
  candidateSuggestions: BankTransactionSuggestionJournalEntry[];
  targetCandidates: number[];
  availableTotal: number;
  transactionTotal: number;
};

export const amountToCents = (amount: number) => Math.round(amount * 100);

export const getJournalDateKey = (entry: BankTransactionSuggestionJournalEntry) =>
  entry.journalDate ?? entry.createdAt.slice(0, 10);

const parseDateKey = (dateKey: string) => {
  const parsed = new Date(`${dateKey}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
};

export const addMonthsKey = (dateKey: string, months: number) => {
  const parsed = parseDateKey(dateKey);
  if (!parsed) {
    return null;
  }
  const next = new Date(
    Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth() + months, parsed.getUTCDate()),
  );
  return next.toISOString().slice(0, 10);
};

export const addDaysKey = (dateKey: string, days: number) => {
  const parsed = parseDateKey(dateKey);
  if (!parsed) {
    return null;
  }
  const next = new Date(parsed.getTime());
  next.setUTCDate(next.getUTCDate() + days);
  return next.toISOString().slice(0, 10);
};

export const insertDateDesc = (profile: string[], dateKey: string) => {
  const next = [...profile];
  let index = 0;
  while (index < next.length && next[index] > dateKey) {
    index += 1;
  }
  next.splice(index, 0, dateKey);
  return next;
};

export const compareDateProfiles = (a: string[], b: string[]) => {
  const length = Math.max(a.length, b.length);
  for (let index = 0; index < length; index += 1) {
    const left = a[index];
    const right = b[index];
    if (left === right) {
      continue;
    }
    if (!left) {
      return -1;
    }
    if (!right) {
      return 1;
    }
    if (left < right) {
      return -1;
    }
    if (left > right) {
      return 1;
    }
  }
  return 0;
};

export const compareSelections = (candidate: SmartLinkSelection, current: SmartLinkSelection) => {
  const profileComparison = compareDateProfiles(candidate.dateProfile, current.dateProfile);
  if (profileComparison !== 0) {
    return profileComparison;
  }
  if (candidate.ids.length !== current.ids.length) {
    return candidate.ids.length - current.ids.length;
  }
  for (let index = 0; index < candidate.ids.length; index += 1) {
    if (candidate.ids[index] !== current.ids[index]) {
      return candidate.ids[index] - current.ids[index];
    }
  }
  return 0;
};

export const findBestSelection = (
  entries: BankTransactionSuggestionJournalEntry[],
  targetCents: number,
) => {
  if (targetCents <= 0) {
    return null;
  }
  const findSignedSelection = () => {
    const normalizedEntries = entries
      .map((entry) => ({
        id: entry.id,
        dateKey: getJournalDateKey(entry),
        remainingCents: amountToCents(entry.remainingAmount),
      }))
      .filter((entry) => entry.remainingCents !== 0)
      .sort((left, right) => {
        if (left.dateKey !== right.dateKey) {
          return left.dateKey.localeCompare(right.dateKey);
        }
        return left.id - right.id;
      });
    if (!normalizedEntries.some((entry) => entry.remainingCents < 0)) {
      return null;
    }

    const maxSignedEntries = 44;
    const maxSignedStates = 40_000;
    const negativeTotalCents = normalizedEntries.reduce(
      (sum, entry) => sum + (entry.remainingCents < 0 ? Math.abs(entry.remainingCents) : 0),
      0,
    );
    const maxPositiveCents = targetCents + negativeTotalCents;
    const boundedEntries = normalizedEntries.filter(
      (entry) => entry.remainingCents < 0 || entry.remainingCents <= maxPositiveCents,
    );
    const buildSubsetMap = (
      subsetEntries: Array<{ dateKey: string; id: number; remainingCents: number }>,
    ) => {
      const subsetMap = new Map<number, SmartLinkSelection>();
      const subsetCount = 2 ** subsetEntries.length;
      for (let mask = 0; mask < subsetCount; mask += 1) {
        let sum = 0;
        const ids: number[] = [];
        let dateProfile: string[] = [];
        for (let index = 0; index < subsetEntries.length; index += 1) {
          if ((mask & (1 << index)) === 0) {
            continue;
          }
          const entry = subsetEntries[index];
          sum += entry.remainingCents;
          ids.push(entry.id);
          dateProfile =
            dateProfile.length === 0
              ? [entry.dateKey]
              : insertDateDesc(dateProfile, entry.dateKey);
        }
        const candidate = { ids, dateProfile };
        const current = subsetMap.get(sum);
        if (!current || compareSelections(candidate, current) < 0) {
          subsetMap.set(sum, candidate);
        }
      }
      return subsetMap;
    };
    const findMeetInMiddleSelection = () => {
      const maxMeetInMiddleEntries = 36;
      if (boundedEntries.length > maxMeetInMiddleEntries) {
        return { selection: null, ambiguous: false };
      }
      const midpoint = Math.ceil(boundedEntries.length / 2);
      const leftMap = buildSubsetMap(boundedEntries.slice(0, midpoint));
      const rightMap = buildSubsetMap(boundedEntries.slice(midpoint));
      let bestSelection: SmartLinkSelection | null = null;
      let exactSelectionCount = 0;
      for (const [leftSum, leftSelection] of leftMap.entries()) {
        const rightSelection = rightMap.get(targetCents - leftSum);
        if (!rightSelection) {
          continue;
        }
        exactSelectionCount += 1;
        const candidate = {
          ids: [...leftSelection.ids, ...rightSelection.ids].sort(
            (left, right) => left - right,
          ),
          dateProfile: [...leftSelection.dateProfile, ...rightSelection.dateProfile].sort(
            (left, right) => right.localeCompare(left),
          ),
        };
        if (!bestSelection || compareSelections(candidate, bestSelection) < 0) {
          bestSelection = candidate;
        }
      }
      return { selection: bestSelection, ambiguous: exactSelectionCount > 1 };
    };
    const meetInMiddleResult = findMeetInMiddleSelection();
    if (meetInMiddleResult.ambiguous) {
      return null;
    }
    if (meetInMiddleResult.selection) {
      return meetInMiddleResult.selection;
    }

    const signedEntries = boundedEntries.slice(0, maxSignedEntries);
    const minAllowedCents = -negativeTotalCents;
    const maxAllowedCents = targetCents + negativeTotalCents;
    let dp = new Map<number, SmartLinkSelection>([[0, { ids: [], dateProfile: [] }]]);

    for (const entry of signedEntries) {
      const nextDp = new Map(dp);
      for (const [sum, selection] of dp.entries()) {
        const nextSum = sum + entry.remainingCents;
        if (nextSum < minAllowedCents || nextSum > maxAllowedCents) {
          continue;
        }
        const candidate: SmartLinkSelection = {
          ids: selection.ids.length === 0 ? [entry.id] : [...selection.ids, entry.id],
          dateProfile:
            selection.dateProfile.length === 0
              ? [entry.dateKey]
              : insertDateDesc(selection.dateProfile, entry.dateKey),
        };
        const current = nextDp.get(nextSum);
        if (!current || compareSelections(candidate, current) < 0) {
          nextDp.set(nextSum, candidate);
        }
      }
      if (nextDp.size > maxSignedStates) {
        const prunedEntries = [...nextDp.entries()]
          .sort((left, right) => {
            const distanceComparison =
              Math.abs(targetCents - left[0]) - Math.abs(targetCents - right[0]);
            if (distanceComparison !== 0) {
              return distanceComparison;
            }
            return compareSelections(left[1], right[1]);
          })
          .slice(0, maxSignedStates);
        dp = new Map(prunedEntries);
      } else {
        dp = nextDp;
      }
    }

    return dp.get(targetCents) ?? null;
  };

  const signedSelection = findSignedSelection();
  if (signedSelection) {
    return signedSelection;
  }

  const maxGreedyEntries = 30;
  const maxGreedyDepth = 8;
  const maxGreedyNodes = 20_000;
  const gcd = (left: number, right: number) => {
    let a = Math.abs(left);
    let b = Math.abs(right);
    while (b !== 0) {
      const remainder = a % b;
      a = b;
      b = remainder;
    }
    return a;
  };
  const groupedEntries = new Map<
    string,
    { dateKey: string; remainingCents: number; ids: number[] }
  >();
  for (const entry of entries) {
    const remainingCents = amountToCents(entry.remainingAmount);
    if (remainingCents <= 0 || remainingCents > targetCents) {
      continue;
    }
    const dateKey = getJournalDateKey(entry);
    const groupKey = `${dateKey}:${remainingCents}`;
    const group = groupedEntries.get(groupKey);
    if (group) {
      group.ids.push(entry.id);
      continue;
    }
    groupedEntries.set(groupKey, { dateKey, remainingCents, ids: [entry.id] });
  }

  const orderedEntries: Array<{ id: number; dateKey: string; remainingCents: number }> = [];
  for (const group of groupedEntries.values()) {
    group.ids.sort((left, right) => left - right);
    const maxCount = Math.min(group.ids.length, Math.floor(targetCents / group.remainingCents));
    for (let index = 0; index < maxCount; index += 1) {
      orderedEntries.push({
        id: group.ids[index],
        dateKey: group.dateKey,
        remainingCents: group.remainingCents,
      });
    }
  }

  orderedEntries.sort((left, right) => {
    if (left.dateKey !== right.dateKey) {
      return left.dateKey.localeCompare(right.dateKey);
    }
    return left.id - right.id;
  });

  let scale = targetCents;
  for (const entry of orderedEntries) {
    scale = gcd(scale, entry.remainingCents);
    if (scale === 1) {
      break;
    }
  }
  const scaledTarget = scale > 1 ? Math.round(targetCents / scale) : targetCents;
  if (scale > 1) {
    for (const entry of orderedEntries) {
      entry.remainingCents = Math.round(entry.remainingCents / scale);
    }
  }

  const findAnyGreedySelection = () => {
    const candidates = [...orderedEntries].sort((left, right) => {
      if (left.remainingCents !== right.remainingCents) {
        return right.remainingCents - left.remainingCents;
      }
      if (left.dateKey !== right.dateKey) {
        return left.dateKey.localeCompare(right.dateKey);
      }
      return left.id - right.id;
    });
    const quickIds: number[] = [];
    let quickProfile: string[] = [];
    let quickSum = 0;
    for (const entry of candidates) {
      const nextSum = quickSum + entry.remainingCents;
      if (nextSum > scaledTarget) {
        continue;
      }
      quickIds.push(entry.id);
      quickProfile =
        quickProfile.length === 0
          ? [entry.dateKey]
          : insertDateDesc(quickProfile, entry.dateKey);
      quickSum = nextSum;
      if (quickSum === scaledTarget) {
        return { ids: quickIds, dateProfile: quickProfile };
      }
    }

    let visited = 0;
    let found: SmartLinkSelection | null = null;

    const dfs = (
      index: number,
      currentSum: number,
      ids: number[],
      profile: string[],
    ) => {
      if (found || visited >= maxGreedyNodes) {
        return;
      }
      visited += 1;
      if (currentSum === scaledTarget) {
        found = { ids, dateProfile: profile };
        return;
      }
      if (index >= candidates.length || ids.length >= maxGreedyDepth) {
        return;
      }
      for (let nextIndex = index; nextIndex < candidates.length; nextIndex += 1) {
        const entry = candidates[nextIndex];
        const nextSum = currentSum + entry.remainingCents;
        if (nextSum > scaledTarget) {
          continue;
        }
        const nextProfile =
          profile.length === 0 ? [entry.dateKey] : insertDateDesc(profile, entry.dateKey);
        dfs(nextIndex + 1, nextSum, [...ids, entry.id], nextProfile);
        if (found) {
          return;
        }
      }
    };

    dfs(0, 0, [], []);
    return found;
  };

  let totalRemainingCents = 0;
  for (const entry of orderedEntries) {
    totalRemainingCents += entry.remainingCents;
  }
  if (totalRemainingCents < scaledTarget) {
    return null;
  }

  if (orderedEntries.length > maxGreedyEntries) {
    const greedySelection = findAnyGreedySelection();
    if (greedySelection) {
      return greedySelection;
    }
  }

  const dp = new Map<number, SmartLinkSelection>();
  dp.set(0, { ids: [], dateProfile: [] });
  let dpKeys = [0];
  let bestSelection: SmartLinkSelection | null = null;
  let bestMaxDate = "";

  for (const entry of orderedEntries) {
    if (bestSelection && entry.dateKey > bestMaxDate) {
      break;
    }
    const nextKeys: number[] = [];
    for (const sum of dpKeys) {
      const selection = dp.get(sum);
      if (!selection) {
        continue;
      }
      const nextSum = sum + entry.remainingCents;
      if (nextSum > scaledTarget) {
        continue;
      }
      const nextIds = selection.ids.length === 0 ? [entry.id] : [...selection.ids, entry.id];
      const nextProfile =
        selection.dateProfile.length === 0
          ? [entry.dateKey]
          : insertDateDesc(selection.dateProfile, entry.dateKey);
      const candidate: SmartLinkSelection = {
        ids: nextIds,
        dateProfile: nextProfile,
      };
      const current = dp.get(nextSum);
      if (!current || compareSelections(candidate, current) < 0) {
        dp.set(nextSum, candidate);
        if (!current) {
          nextKeys.push(nextSum);
        }
      }
    }
    if (nextKeys.length > 0) {
      dpKeys = dpKeys.concat(nextKeys);
    }
    const targetSelection = dp.get(scaledTarget) ?? null;
    if (targetSelection) {
      bestSelection = targetSelection;
      bestMaxDate = targetSelection.dateProfile[0] ?? "";
    }
  }

  return dp.get(scaledTarget) ?? null;
};

export const selectSmartLink = ({
  suggestions,
  transactionAmount,
  transactionDate,
  transactionCreatedAt,
  channelBalance,
  cutoffDays = 60,
}: SmartLinkRequest): SmartLinkResult => {
  const transactionDateKey = transactionDate ?? transactionCreatedAt.slice(0, 10);
  const cutoffKey = addDaysKey(transactionDateKey, cutoffDays);
  const candidateSuggestions = cutoffKey
    ? suggestions.filter((entry) => getJournalDateKey(entry) <= cutoffKey)
    : suggestions;

  const availableTotal = transactionAmount + Math.max(channelBalance, 0);
  const availableCents = amountToCents(availableTotal);
  const transactionCents = amountToCents(transactionAmount);
  const targetCandidates =
    availableCents !== transactionCents ? [availableCents, transactionCents] : [transactionCents];

  let selection: SmartLinkSelection | null = null;
  for (const target of targetCandidates) {
    selection = findBestSelection(candidateSuggestions, target);
    if (selection) {
      break;
    }
  }

  return {
    selection,
    candidateSuggestions,
    targetCandidates,
    availableTotal,
    transactionTotal: transactionAmount,
  };
};
